import { getCore } from '../../../client/core/getCore';
import type { DynamicClient } from '../../../client/types';
import { isUserMissingMfaAuth } from '../../mfa/isUserMissingMfaAuth';
import { getWalletAccounts } from '../../wallets/getWalletAccounts';
import { findWaasWalletProviderByChain } from '../findWaasWalletProviderByChain';
import { isWaasWalletAccount } from '../isWaasWalletAccount';

/**
 * On login, boots the WaaS client once per chain so the SDK eagerly recovers
 * key shares itself (the `eagerlyRecoverKeyShares` capability) instead of the
 * host looping per wallet. Booting calls `initialize()`, which sends the auth
 * token the iframe needs to self-recover.
 *
 * @notInstrumented
 */
export const initializeWaasClientsForEagerRecovery = async (
  client: DynamicClient
): Promise<void> => {
  /**
   * Booting triggers recovery, which fails if the user is missing the MFA auth.
   * This early return avoids attempting a call we know will fail and just adds
   * noise to the logs.
   */
  if (isUserMissingMfaAuth(client)) {
    return;
  }

  const waasChains = new Set(
    getWalletAccounts(client)
      .filter((walletAccount) => isWaasWalletAccount({ walletAccount }))
      .map((walletAccount) => walletAccount.chain)
  );

  await Promise.all(
    [...waasChains].map(async (chain) => {
      const provider = findWaasWalletProviderByChain({ chain }, client);

      /**
       * The environment might not have the embedded wallet extensions installed.
       * In that case there is no provider for the chain and nothing to boot.
       */
      if (!provider) return;

      try {
        await provider.getWaasClient();
      } catch (error) {
        getCore(client).logger.warn(
          '[initializeWaasClientsForEagerRecovery] failed to boot waas client',
          error
        );
      }
    })
  );
};
